import { AuthedLayout } from '@/components/AuthedLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LandingRoute } from '@/components/LandingRoute';
import { NotFound } from '@/components/NotFound';
import { RootGate } from '@/components/RootGate';
import { Maintenance } from '@/pages/Maintenance';
import { adminRoutes } from '@/routes/admin.routes';
import { authRoutes } from '@/routes/auth.routes';
import { catalogRoutes } from '@/routes/catalog.routes';
import { dashboardRoutes } from '@/routes/dashboard.routes';
import { meRoutes } from '@/routes/me.routes';
import { metricsRoutes } from '@/routes/metrics.routes';
import { submissionsRoutes } from '@/routes/submissions.routes';
import { teamsRoutes } from '@/routes/teams.routes';
import type { RouteHandle } from '@/routes/types';
import type { RouteObject } from 'react-router-dom';

/**
 * Routes are organized into three layers at the top:
 *
 *  1. `RootGate` — universal maintenance check. Anon visitors hitting any
 *     path during maintenance see the maintenance page (admins holding
 *     `maintenance:bypass` keep their escape hatch).
 *  2. Public-by-design routes: `/maintenance`, `/` (landing), and the
 *     `/auth/*` surfaces (`/auth/login`, `/auth/callback`, `/auth/logout`)
 *     from `authRoutes`. No chrome, no auth gate. Mount directly under
 *     RootGate.
 *  3. Authed routes (pathless `AuthedLayout`): everything else, gated on
 *     `usePrincipal().isAuthenticated`, wrapped in `AppShell` chrome,
 *     permissions enforced per route handle by `RequirePermissions`.
 *
 * Per the route-module convention, this file ONLY aggregates. Each feature
 * owns its route definitions in `src/routes/<feature>.routes.tsx`,
 * including `handle.crumb` and `handle.requiredPermissions`. Adding a
 * top-level authed route means creating a new module + listing it inside
 * `AuthedLayout`'s children — no other edits to this file.
 *
 * Exported as a route config (not a constructed router) so tests can mount
 * via `createMemoryRouter(routes, { initialEntries: [...] })` and App.tsx
 * can construct a real browser router via `useMemo`.
 */
export const routes: RouteObject[] = [
  {
    element: <RootGate />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/maintenance', element: <Maintenance /> },
      { path: '/', element: <LandingRoute /> },
      ...authRoutes,
      {
        element: <AuthedLayout />,
        handle: { crumb: 'Home', crumbTo: '/dashboard' } satisfies RouteHandle,
        children: [
          ...dashboardRoutes,
          ...catalogRoutes,
          ...teamsRoutes,
          ...submissionsRoutes,
          ...metricsRoutes,
          ...meRoutes,
          ...adminRoutes,
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
];
